import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import InputText from "../../../../components/Inputs/InputText";
import { useNotification } from "../../../../Utils/hooks";
import { getTFGTitulations } from "../../../../services/TFG";
import InputAutoComplete from "../../../../components/Inputs/InputAutoComplete";

const InstructorAllocationDialog = ({
  selectedCoord,
  selectedTerm,
  visible,
  onHide,
  onSave,
  selectedAllocation,
}) => {
  const isCreate = !selectedAllocation;
  const [termAllocations, setTermAllocations] = useState([]);
  const [allocation, setAllocation] = useState();
  const [degrees, setDegrees] = useState([]);
  const notification = useNotification();

  const getSelectableDegrees = (degrees) => {
    if (!termAllocations) return [];
    const currentAllocationDegrees = allocation?.degrees.map(d => d.externalId) ?? []
    const termAllocationDegrees = termAllocations
      .map((a) => (a.degrees ?? []).map((d) => d.externalId))
      .flat();
    const selectedDegrees = [...currentAllocationDegrees, ...termAllocationDegrees]
    return degrees.filter((d) => !selectedDegrees.includes(d.value));
  };

  const selectableDegrees = useMemo(
    () => getSelectableDegrees(degrees),
    [termAllocations]
  );

  // No funciona añadir esto como valor por defecto del useState
  // porque solo se aplica en el primer renderizado.
  useEffect(() => {
    setAllocation(selectedAllocation);
  }, [selectedAllocation]);

  useEffect(() => {
    if (!selectedCoord) {
      setTermAllocations([]);
    } else {
      const allocations = selectedCoord.allocations.filter(
        (item) => item.academicYearId === selectedTerm
      );
      if (!allocations) return;
      setTermAllocations(allocations);
    }
  }, [selectedCoord, selectedTerm]);

  useEffect(() => {
    async function retrieveTitulations(selectedTerm) {
      try {
        const result = await getTFGTitulations(selectedTerm, true).then((res) =>
          res.map((item) => ({ value: item.externalId, label: item.name }))
        );
        if (!result) throw new Error();
        if (!result.length) {
          notification(
            "error",
            "No se han encontrado titulaciones con áreas temáticas disponibles"
          );
        }
        setDegrees(result);
      } catch {
        notification("error", "Ha habido un error al obtener las titulaciones");
      }
    }

    if (selectedTerm) retrieveTitulations(selectedTerm);
  }, [notification, selectedTerm]);

  /* const onAddDegreeLimit = (degree, limit) => {
    setAllocation(prev => ({
      ...prev,
      degrees: prev?.degrees ? [...prev.degrees, degree] : [degree],
      limit: prev?.limit || limit
    }));
  } */

  const initAllocation = () => {
    setAllocation((prev) => ({
      ...prev,
      degrees: [],
      limit: 0,
    }));
  };

  const onRemoveDegreeLimit = (degreesToRemove) => {
    // TODO Falta endpoint
    const degreeIds = degreesToRemove.map((d) => d.externalId);
    setAllocation((prev) => ({
      ...prev,
      degrees: prev.degrees.filter((d) => !degreeIds.includes(d)),
    }));
  };

  const onEditDegreeLimit = (degrees, limit) => {
    console.debug("degrees", degrees);
    setAllocation((prev) => ({ ...prev, degrees, limit }));
  };

  const onSaveDialog = () => {
    if (
      !allocation.degrees ||
      !allocation.degrees.length ||
      !allocation.limit
    ) {
      notification("error", "Por favor, rellene todos los campos");
      return;
    }

    onSave(allocation, isCreate);
  };

  // Transform label-value pairs into the right fields
  const onAutocompleteChange = (degreeOptions) => {
    console.debug('onAutocompleteChange', degreeOptions)
    const mappedDegrees = degreeOptions?.map(d => ({
      externalId: d.value,
      name: d.label
    })) ?? []
    onEditDegreeLimit(mappedDegrees, allocation.limit)
  }

  return (
    <Dialog
      className="instructor-allocation-dialog"
      open={visible}
      onClose={onHide}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Modificar asignación de {selectedCoord?.instructorName}
      </DialogTitle>
      <DialogContent>
        <div className="degree-limits">
          <div className="header">
            <h4>Límites por titulación</h4>
            {!allocation && (
              <Tooltip title="Añadir" aria-label="add">
                <IconButton
                  disabled={!selectableDegrees.length}
                  aria-label="add"
                  onClick={initAllocation}
                >
                  <Icon className="add-icon">add</Icon>
                </IconButton>
              </Tooltip>
            )}
          </div>
          <Grid container spacing={2}>
            {!allocation && (
              <p className="empty-list">No hay límites configurados</p>
            )}
            {allocation && (
              <>
                <Grid item xs={12} md={5}>
                  <InputAutoComplete
                    key={`degree`}
                    name={`degree`}
                    multiple={true}
                    valueKey="externalId"
                    value={allocation.degrees.map((d) => ({
                      value: d.externalId,
                      label: d.name,
                    }))}
                    onChange={onAutocompleteChange}
                    label="Titulación"
                    options={degrees}
                    disabled={!degrees.length}
                    filterOptions={getSelectableDegrees}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <InputText
                    key={`limit`}
                    name={`limit`}
                    value={allocation.limit}
                    onChange={(e) =>
                      onEditDegreeLimit(allocation.degrees, e.target.value)
                    }
                    label="Límite"
                    inputProps={{ type: "number", min: 0 }}
                    // placeholder="Lugar donde se realizará la defensa"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Tooltip title="Eliminar" aria-label="delete">
                    <IconButton
                      aria-label="delete"
                      onClick={() => onRemoveDegreeLimit(allocation.degrees)}
                    >
                      <Icon className="delete-icon">delete</Icon>
                    </IconButton>
                  </Tooltip>
                </Grid>
              </>
            )}
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="contained" color="primary" onClick={onSaveDialog}>
          Guardar cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstructorAllocationDialog;
